// Affiliate tracking: store/retrieve affiliate code in localStorage scoped by eventId

const STORAGE_PREFIX = 'affiliate_'
const TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

interface StoredAffiliate {
  code: string
  expiresAt: number
}

export function setAffiliateCode(eventId: string, code: string): void {
  try {
    const data: StoredAffiliate = {
      code,
      expiresAt: Date.now() + TTL_MS,
    }
    localStorage.setItem(`${STORAGE_PREFIX}${eventId}`, JSON.stringify(data))
  } catch {
    // localStorage unavailable (SSR, private browsing, etc.)
  }
}

export function getAffiliateCode(eventId: string): string | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${eventId}`)
    if (!raw) return null
    const data: StoredAffiliate = JSON.parse(raw)
    if (Date.now() > data.expiresAt) {
      localStorage.removeItem(`${STORAGE_PREFIX}${eventId}`)
      return null
    }
    return data.code
  } catch {
    return null
  }
}

/**
 * The code to credit for a whole cart.
 *
 * A cart can hold more than one event — checkout has an "Add tickets from
 * another event" button — so every event in it is considered, in cart order,
 * and the first one carrying a stored code wins. Reading only the first item's
 * event lost the sale outright whenever the affiliate's event was not the one
 * added first.
 */
export function getAffiliateCodeForEvents(eventIds: (string | null | undefined)[]): string | null {
  const seen = new Set<string>()
  for (const eventId of eventIds) {
    if (!eventId || seen.has(eventId)) continue
    seen.add(eventId)
    const code = getAffiliateCode(eventId)
    if (code) return code
  }
  return null
}

export function clearAffiliateCode(eventId: string): void {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${eventId}`)
  } catch {
    // localStorage unavailable
  }
}
